import React from "react";
import { motion } from "framer-motion";
import { ContextualHintCard } from "@rapid/components/shared/ContextualHintCard";
import { IconFrame } from "@scripts/react/components/IconFrame";

type PromptingTipProps = { icon: string; title: string; description: string | React.ReactNode };

const getPromptingTips = (onOpenLabelingGuide?: () => void): PromptingTipProps[] => [
    {
        icon: "fad fa-bullseye-arrow",
        title: "Be Specific and Concise",
        description: `Short, specific noun phrases work best. Instead of "vehicle," try "yellow school bus".`
    },
    {
        icon: "fad fa-text",
        title: "Iterate and Refine",
        description: `If one prompt doesn't work, try alternatives. For example, "person" vs "human" vs "pedestrian" may yield different results.`
    },
    {
        icon: "fad fa-list",
        title: "Use Common Terms",
        description: `Simple, commonly-used words work best. Avoid jargon or overly technical terms.`
    },
    {
        icon: "fad fa-vector-square",
        title: "Draw a Box Around the Object",
        description: (
            <>
                If you're having trouble finding an object using just text prompting, draw a box
                around it instead.{" "}
                {onOpenLabelingGuide && (
                    <button
                        onClick={onOpenLabelingGuide}
                        className="link text-brand hover:text-brand-hover"
                    >
                        See how
                    </button>
                )}
            </>
        )
    }
];

interface PromptingTipsCardProps {
    isOpen: boolean;
    onClose: () => void;
    anchorElement?: HTMLElement | null;
    onOpenLabelingGuide?: () => void;
}

function PromptingTipsContent({ tips }: { tips: PromptingTipProps[] }) {
    return (
        <div className="flex flex-1 flex-col gap-5 overflow-y-auto">
            {tips.map((tip, index) => (
                <motion.div
                    key={index}
                    className="flex flex-col gap-3"
                    initial={{ opacity: 0, y: 8, filter: "blur(5px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{ duration: 0.3, ease: "easeOut", delay: index * 0.1 }}
                >
                    <div className="flex gap-3">
                        {/* @ts-expect-error - IconFrame component type issue */}
                        <IconFrame icon={tip.icon} size="md" color="gray" />
                        <div className="flex flex-col gap-0.5">
                            <h3 className="text-sm font-semibold text-primary">{tip.title}</h3>
                            <p className="text-xs leading-relaxed text-tertiary">
                                {tip.description}
                            </p>
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
}

export function PromptingTipsCard({
    isOpen,
    onClose,
    anchorElement,
    onOpenLabelingGuide
}: PromptingTipsCardProps) {
    const tips = getPromptingTips(onOpenLabelingGuide);
    return (
        <ContextualHintCard isOpen={isOpen} onClose={onClose} anchorElement={anchorElement}>
            <PromptingTipsContent tips={tips} />
        </ContextualHintCard>
    );
}
